import type { ReactNode, ThHTMLAttributes, TdHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

/**
 * A plain semantic table.
 *
 * Real <table> markup rather than a grid of divs, so screen readers announce
 * row and column relationships and the browser's own find-in-page works.
 */
export function Table({
  caption,
  children,
  className,
}: {
  caption: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className="overflow-x-auto">
      <table className={cn("w-full border-collapse text-sm", className)}>
        <caption className="sr-only">{caption}</caption>
        {children}
      </table>
    </div>
  );
}

/** Accepts the standard <th> attributes, so callers can add title, colSpan, etc. */
export function Th({
  children,
  className,
  scope = "col",
  ...props
}: ThHTMLAttributes<HTMLTableCellElement> & {
  children: ReactNode;
  scope?: "col" | "row";
}) {
  return (
    <th
      scope={scope}
      {...props}
      className={cn(
        "sticky top-0 z-10 whitespace-nowrap border-b border-slate-200 bg-slate-50 px-3 py-2 text-left font-semibold text-slate-700",
        className,
      )}
    >
      {children}
    </th>
  );
}

/** Accepts the standard <td> attributes, so callers can add title, colSpan, etc. */
export function Td({
  children,
  className,
  ...props
}: TdHTMLAttributes<HTMLTableCellElement> & { children: ReactNode }) {
  return (
    <td
      className={cn("border-b border-slate-100 px-3 py-2 align-top", className)}
      {...props}
    >
      {children}
    </td>
  );
}
